import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from 'tss-react/mui';

import {
  Card,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { useTable, usePagination, useSortBy } from 'react-table';

import TableCellAction from 'components/table/TableCellAction';
import ConfirmationDialog from 'components/modals/ConfirmationDialog';
import { filterTypes } from './filterTypes';

const useStyles = makeStyles()((theme) => ({
  root: {},
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 1050,
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  actions: {
    justifyContent: 'flex-end',
  },
  selectableRow: {
    cursor: 'pointer',
  },
  tableActionMargin: { margin: '0 0.5rem' },
  tableActionMargin1: { paddingTop: '0.8rem', paddingBottom: '0.7rem' },
  customHeaderCell: {
    padding: '10px 16px',
    borderBottom: 'none',
    color: 'rgb(99, 115, 129)',
    backgroundColor: 'rgb(244, 246, 248)',
  },
}));

// COMPONENT
function SimpleNoFilterReactTable({
  onSelectCallback,
  dataSource,
  columns,
  initialState,
  onDeleteCallback,
  onEditCallback,
  hideEdit,
  hideDelete,
  customActions,
  actionsDisabled,
  id,
  size,
  deleteMessageCallback,
  getCellProps,
  getHeaderProps,
}) {
  const { classes, cx } = useStyles();
  const [modalDelete, setModalDelete] = useState({ open: false });

  // REACT TABLE
  const memoFilterTypes = React.useMemo(() => filterTypes, []);

  const {
    getTableProps,
    getTableBodyProps,
    headers,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    state: { pageIndex, pageSize },
    gotoPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data: dataSource,
      initialState,
      filterTypes: memoFilterTypes,
    },
    useSortBy,
    usePagination,
  );

  const handleDeleteConfirmation = () => {
    onDeleteCallback(modalDelete.objectSelected);
    setModalDelete((prev) => ({ ...prev, open: false }));
  };

  const handleDelete = (objectToDelete) => {
    setModalDelete({
      open: true,
      objectSelected: objectToDelete.original,
    });
  };

  const handleEdit = (object) => {
    onEditCallback(object.original);
  };

  return (
    <React.Fragment>
      <ConfirmationDialog
        title="Exclusão"
        message={
          deleteMessageCallback
            ? deleteMessageCallback(modalDelete.objectSelected)
            : 'Você tem certeza que deseja deletar?'
        }
        open={modalDelete.open}
        onConfirm={handleDeleteConfirmation}
        onClose={() => {
          setModalDelete((prev) => ({ ...prev, open: false }));
        }}
      />

      <Card className={classes.root}>
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            {/* TABLE */}
            <div>
              <Table {...getTableProps()} id={id} size={size}>
                <TableHead>
                  <TableRow>
                    {headers.map((column) => {
                      if (getHeaderProps) {
                        const respHeaderProps = getHeaderProps(column);
                        if (respHeaderProps) {
                          return (
                            <TableCell
                              {...column.getHeaderProps([respHeaderProps])}
                              className={classes.customHeaderCell}
                            >
                              <TableSortLabel
                                active={column.isSorted && column.isSortedDesc != null}
                                direction={column.isSortedDesc ? 'desc' : 'asc'}
                                {...column.getSortByToggleProps()}
                              >
                                {column.render('Header')}
                              </TableSortLabel>
                              <div>{column.canFilter ? column.render('Filter') : null}</div>
                            </TableCell>
                          );
                        }
                      }
                      return (
                        <TableCell {...column.getHeaderProps()} className={classes.customHeaderCell}>
                          <TableSortLabel
                            active={column.isSorted && column.isSortedDesc != null}
                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                            {...column.getSortByToggleProps()}
                          >
                            {column.render('Header')}
                          </TableSortLabel>
                          <div>{column.canFilter ? column.render('Filter') : null}</div>
                        </TableCell>
                      );
                    })}
                    {customActions &&
                      customActions.map((a) => (
                        <TableCell key={a.title} className={classes.customHeaderCell} />
                      ))}
                    {!hideEdit && <TableCell className={classes.customHeaderCell} />}
                    {!hideDelete && <TableCell className={classes.customHeaderCell} />}
                  </TableRow>
                </TableHead>
                <TableBody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <TableRow
                        className={cx(classes.tableRow, onSelectCallback && classes.selectableRow)}
                        hover={onSelectCallback != null}
                        {...row.getRowProps()}
                        onClick={() => {
                          if (onSelectCallback) {
                            onSelectCallback(row);
                          }
                        }}
                      >
                        {row.cells.map((cell) => {
                          if (getCellProps) {
                            const respCellProps = getCellProps(cell);
                            if (respCellProps) {
                              return (
                                <TableCell {...cell.getCellProps([respCellProps])}>
                                  {cell.render('Cell')}
                                </TableCell>
                              );
                            }
                          }
                          return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>;
                        })}
                        {customActions &&
                          customActions.map((action) => {
                            if (action.hiddable) {
                              const isActionHidden = row.original[`hidden_${action.id}`];
                              if (isActionHidden) {
                                return (
                                  <TableCell key={action.title} className={classes.tableActionMargin1}>
                                    <IconButton color="primary" disabled size="large">
                                      {' '}
                                    </IconButton>
                                  </TableCell>
                                );
                              }
                            }
                            return (
                              <TableCellAction
                                key={action.title}
                                title={action.title}
                                onClick={(e) => {
                                  action.handler(row, e);
                                }}
                                buttonClassName={classes.tableActionMargin}
                                disabled={actionsDisabled}
                              >
                                {action.icon}
                              </TableCellAction>
                            );
                          })}
                        {!hideEdit && (
                          <TableCellAction
                            title="Editar"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleEdit(row);
                            }}
                            disabled={actionsDisabled}
                          >
                            <EditIcon color="primary" />
                          </TableCellAction>
                        )}
                        {!hideDelete && (
                          <TableCellAction
                            title="Deletar"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDelete(row);
                            }}
                            disabled={actionsDisabled}
                          >
                            <DeleteIcon color="error" />
                          </TableCellAction>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {dataSource.length === 0 && (
                <Typography style={{ margin: '10px' }} variant="subtitle2" gutterBottom>
                  Nenhum resultado encontrado.
                </Typography>
              )}
            </div>
          </PerfectScrollbar>
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            labelRowsPerPage="Linhas por página"
            component="div"
            count={dataSource.length}
            onPageChange={(event, page) => {
              gotoPage(page);
            }}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
            page={pageIndex}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardActions>
      </Card>
    </React.Fragment>
  );
}

SimpleNoFilterReactTable.defaultProps = {
  onSelectCallback: null,
  hideEdit: false,
  hideDelete: false,
  onEditCallback: () => {},
  onDeleteCallback: () => {},
  actionsDisabled: false,
  size: 'small',
  deleteMessageCallback: null,
};

SimpleNoFilterReactTable.propTypes = {
  onSelectCallback: PropTypes.func,
  hideEdit: PropTypes.bool,
  hideDelete: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  onEditCallback: PropTypes.func,
  onDeleteCallback: PropTypes.func,
  dataSource: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  initialState: PropTypes.object.isRequired,
  customActions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      handler: PropTypes.func.isRequired,
      icon: PropTypes.element.isRequired,
    }),
  ),
  deleteMessageCallback: PropTypes.func,
};
export default SimpleNoFilterReactTable;
